/** @jsx jsx */
import React, { type MouseEvent, useCallback, useState, useEffect, type ReactNode } from 'react';
import { jsx, css } from '@compiled/react';
import { di } from 'react-magnetic-di';
import {
	buildEditWorkflowJSPUrl,
	getLegacyEditorLink,
	getNewEditorLink,
} from '@jira/project-settings__project-configuration-commons/src/model/utils.tsx';
import { Label } from '@atlaskit/form';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { ModalBody, ModalTitle, ModalHeader, ModalFooter } from '@atlaskit/modal-dialog';
import { Inline, xcss, Stack, Box } from '@atlaskit/primitives';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import { Text } from '@atlaskit/primitives/compiled';
import Lozenge from '@atlaskit/lozenge';
import WarningIcon from '@atlaskit/icon/core/migration/status-warning--warning';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import type { ProjectType } from '@atlassian/jira-common-constants/src/index.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { useIntl, FormattedMessage, defineMessages } from '@atlassian/jira-intl';
import { createRouterSelector } from '@atlassian/react-resource-router';
import WorkflowBuilderSplash from '@atlassian/jira-illustrations/src/ui/adg4/jira/spots/other/components/workflow-builder-splash/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	NEW_EDITOR,
	LEGACY_EDITOR,
	type EditorType,
	WorkflowPublicationStatus,
} from '../../common/types.tsx';
import { useChangePreferenceFlag } from '../../controllers/change-preference-flag/index.tsx';
import { useNewWorkflowEditorPreference } from '../../controllers/editor-preference/index.tsx';
import { useOptOutFeedbackDialog } from '../../controllers/opt-out-feedback/index.tsx';
import {
	changeDefaultWorkflowEditorToLegacy,
	changeDefaultWorkflowEditorToNew,
} from '../../services/set-workflow-editor-preference/index.tsx';
import {
	setShowNewEditorSuccessFlag,
	setShowOldEditorSuccessFlag,
} from '../../services/update-preference-session/index.tsx';

const messages = defineMessages({
	title: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.title',
		defaultMessage: 'Change workflow editor preference',
		description: 'Heading for the default workflow modal.',
	},

	instruction: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.instruction',
		defaultMessage: 'Preferred workflow editor',
		description: 'Instruction text to choose default editor.',
	},

	workflowEditorNewLabel: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.workflow-editor-new-label',
		defaultMessage: 'Workflow editor',
		description: 'Label of workflow editor new option.',
	},

	workflowEditorOldLabel: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.workflow-editor-old-label',
		defaultMessage: 'Workflow editor',
		description: 'Label of workflow editor old option.',
	},

	workflowEditorNewDescription: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.workflow-editor-new-description',
		defaultMessage:
			'Simplified setup and changes, all in one screen. Easier updates. Bonus <strong>Workflow builder</strong> agent support for AI enabled sites.',
		description: 'Description of workflow editor new option.',
	},

	workflowEditorOldDescription: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.workflow-editor-old-description',
		defaultMessage:
			'Keep using the old editor until it’s no longer available from  early 2026. Any unsaved drafts in the old editor will be lost after this time.',
		description: 'Description of workflow editor old option.',
	},

	workflowEditorNewLozenge: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.workflow-editor-new-lozenge',
		defaultMessage: 'New',
		description: 'Label for workflow editor new icon.',
	},

	workflowEditorOldLozenge: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.workflow-editor-old-lozenge',
		defaultMessage: 'Old',
		description: 'Label for workflow editor old icon.',
	},

	cancelButton: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.cancel-button',
		defaultMessage: 'Cancel',
		description: 'Label for back button.',
	},

	confirmButton: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.confirm-button',
		defaultMessage: 'Confirm',
		description: 'Label for confirm button.',
	},

	updateButton: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.update-button',
		defaultMessage: 'Update',
		description: 'Label for update button.',
	},

	warning: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.warning-label',
		defaultMessage: 'Warning',
		description: 'Label for the warning icon in the workflow editor preference dialog.',
	},

	draftUnpublishedWarning: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.draft-unpublished-warning',
		defaultMessage:
			'Publish your draft workflow before continuing so that your edits will be visible in the new workflow editor.',
		description:
			'A warning message informing users to publish their draft their workflow before switching workflow editors as otherwise the changes will not be reflected in the new editor.',
	},

	dirtyLossWarning: {
		id: 'workflow-editor-preference-settings.default-workflow-editor-dialog.dirty-loss-warning',
		defaultMessage:
			'You’ve made changes to {workflowName}. To save those changes select {cancel}, then {update} workflow before continuing.',
		description:
			'A warning message informing users to save any changes to their workflow before switching workflow editors as they will otherwise be lost.',
	},
});

type Props = {
	onClose: () => void;
	onOpenDefaultEditorDialog?: () => void;
	workflowName: string;
	workflowId: string | undefined;
	projectType?: ProjectType;
	workflowPublicationStatus?: WorkflowPublicationStatus;
	shouldRedirectToDraftMode?: boolean;
	returnFocusRef?: React.RefObject<HTMLElement>;
};

const DefaultWorkflowEditorDialogInner = ({
	workflowName,
	projectType,
	onClose,
	onOpenDefaultEditorDialog,
	workflowId,
	returnFocusRef,
	workflowPublicationStatus = WorkflowPublicationStatus.None,
	shouldRedirectToDraftMode = true,
}: Props) => {
	di(window);
	const { formatMessage } = useIntl();
	const [selectedWorkflowEditor, setSelectedWorkflowEditor] = useState<
		EditorType | null | undefined
	>(null);
	const { route } = createRouterSelector((state) => state)();
	const { userPreference, shouldUseNewWorkFlowEditor, isLoading } =
		useNewWorkflowEditorPreference();
	const { showNewEditorSuccessFlag, showOldEditorSuccessFlag, showErrorFlag } =
		useChangePreferenceFlag();

	useEffect(() => {
		if (isLoading) return;
		setSelectedWorkflowEditor(
			userPreference || (shouldUseNewWorkFlowEditor ? NEW_EDITOR : LEGACY_EDITOR),
		);
	}, [userPreference, setSelectedWorkflowEditor, shouldUseNewWorkFlowEditor, isLoading]);

	const redirectToLegacyEditorAndShowSuccessFlag = useCallback(() => {
		if (route?.name === 'global-settings-issues-workflows-pages') {
			showOldEditorSuccessFlag(onOpenDefaultEditorDialog);
		} else {
			// set flag to show success flag in session storage when user is redirected to legacy editor
			setShowOldEditorSuccessFlag();

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.location.assign(
				shouldRedirectToDraftMode
					? buildEditWorkflowJSPUrl(workflowName)
					: getLegacyEditorLink(workflowName),
			);
		}
	}, [
		route?.name,
		shouldRedirectToDraftMode,
		showOldEditorSuccessFlag,
		workflowName,
		onOpenDefaultEditorDialog,
	]);

	const redirectToNewEditorAndShowSuccessFlag = useCallback(() => {
		if (route?.name === 'global-settings-issues-workflow-edit') {
			showNewEditorSuccessFlag();
		} else {
			// set flag to show success flag in session storage when user is redirected to new editor
			setShowNewEditorSuccessFlag();

			workflowId &&
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
				window.location.assign(getNewEditorLink(workflowId));
		}
	}, [route?.name, showNewEditorSuccessFlag, workflowId]);

	const { renderFeedbackDialog } = useOptOutFeedbackDialog({
		projectType,
		onClose: redirectToLegacyEditorAndShowSuccessFlag,
	});

	const onSelectWorkflowEditor = () => setSelectedWorkflowEditor(NEW_EDITOR);
	const onSelectWorkflowEditorLegacy = () => setSelectedWorkflowEditor(LEGACY_EDITOR);
	const onConfirmButtonClicked = useCallback(
		async (_: MouseEvent<HTMLButtonElement>, analyticsEvent: UIAnalyticsEvent) => {
			try {
				if (selectedWorkflowEditor === LEGACY_EDITOR) {
					await changeDefaultWorkflowEditorToLegacy(analyticsEvent, workflowPublicationStatus);
					renderFeedbackDialog();
				}
				if (selectedWorkflowEditor === NEW_EDITOR) {
					await changeDefaultWorkflowEditorToNew(analyticsEvent, workflowPublicationStatus);
					redirectToNewEditorAndShowSuccessFlag();
				}
				onClose();
			} catch (error) {
				showErrorFlag();
			}
		},
		[
			selectedWorkflowEditor,
			onClose,
			renderFeedbackDialog,
			redirectToNewEditorAndShowSuccessFlag,
			showErrorFlag,
			workflowPublicationStatus,
		],
	);

	const shouldShowWarningMessage =
		workflowPublicationStatus === WorkflowPublicationStatus.Draft ||
		workflowPublicationStatus === WorkflowPublicationStatus.Dirty;

	return (
		<ShortcutScope>
			<ModalDialog
				messageId="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.modal-dialog"
				messageType="transactional"
				onClose={onClose}
				width="531px"
				shouldCloseOnOverlayClick={fg('jif_jam_wfe_ui_fixes')}
				shouldReturnFocus={returnFocusRef}
			>
				<ModalHeader hasCloseButton>
					<ModalTitle testId="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.title">
						<Box xcss={headingStyles}>{formatMessage(messages.title)}</Box>
					</ModalTitle>
				</ModalHeader>
				<ModalBody>
					<fieldset>
						<legend css={legendStyles}>
							<Heading size="xsmall" as="h2">
								{formatMessage(messages.instruction)}
							</Heading>
						</legend>
						<Stack space="space.100">
							<Inline alignBlock="center" space="space.200">
								<Radio
									isChecked={selectedWorkflowEditor === NEW_EDITOR}
									id="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.workflow-editor-new"
									testId="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.workflow-editor-new"
									onClick={onSelectWorkflowEditor}
									value={NEW_EDITOR}
									name="workflowEditor"
								/>
								<Label htmlFor="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.workflow-editor-new">
									<Inline alignBlock="center" space="space.100" xcss={boxStyles}>
										<Stack space="space.100">
											<Inline alignBlock="center" space="space.100">
												<Heading size="xsmall" as="h3">
													{formatMessage(messages.workflowEditorNewLabel)}
												</Heading>
												<Lozenge appearance="new">
													{formatMessage(messages.workflowEditorNewLozenge)}
												</Lozenge>
											</Inline>
											<Text as="p">
												{formatMessage(messages.workflowEditorNewDescription, {
													strong: (chunks: ReactNode) => <Text as="strong">{chunks}</Text>,
												})}
											</Text>
										</Stack>
										<WorkflowBuilderSplash alt="" width="96" />
									</Inline>
								</Label>
							</Inline>
							<Inline alignBlock="center" space="space.200">
								<Radio
									isChecked={selectedWorkflowEditor === LEGACY_EDITOR}
									id="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.workflow-editor-old"
									testId="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.workflow-editor-old"
									onClick={onSelectWorkflowEditorLegacy}
									value={LEGACY_EDITOR}
									name="workflowEditor"
								/>
								<Label htmlFor="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.workflow-editor-old">
									<Stack space="space.100" xcss={boxStyles}>
										<Inline alignBlock="center" space="space.100">
											<Heading size="xsmall" as="h3">
												{formatMessage(messages.workflowEditorOldLabel)}
											</Heading>
											<Lozenge>{formatMessage(messages.workflowEditorOldLozenge)}</Lozenge>
										</Inline>
										<Text as="p">{formatMessage(messages.workflowEditorOldDescription)}</Text>
									</Stack>
								</Label>
							</Inline>
						</Stack>
					</fieldset>
					{shouldShowWarningMessage && (
						<Box role="alert" xcss={warningContainerStyles}>
							<WarningIcon
								LEGACY_size="medium"
								spacing="spacious"
								label={formatMessage(messages.warning)}
								color={token('color.text.warning')}
							/>
							<Box xcss={warningTextStyles}>
								{workflowPublicationStatus === WorkflowPublicationStatus.Draft && (
									<FormattedMessage {...messages.draftUnpublishedWarning} />
								)}
								{workflowPublicationStatus === WorkflowPublicationStatus.Dirty && (
									<FormattedMessage
										{...messages.dirtyLossWarning}
										values={{
											workflowName: <b>{workflowName}</b>,
											update: <i>{formatMessage(messages.updateButton)}</i>,
											cancel: <i>{formatMessage(messages.cancelButton)}</i>,
										}}
									/>
								)}
							</Box>
						</Box>
					)}
				</ModalBody>
				<ModalFooter>
					<Button onClick={onClose} appearance="subtle">
						{formatMessage(messages.cancelButton)}
					</Button>
					<Button
						onClick={onConfirmButtonClicked}
						isDisabled={userPreference === selectedWorkflowEditor}
						appearance="primary"
						testId="workflow-editor-preference-settings.ui.default-workflow-editor-dialog.confirm-button"
					>
						{formatMessage(messages.confirmButton)}
					</Button>
				</ModalFooter>
			</ModalDialog>
		</ShortcutScope>
	);
};

export const DefaultWorkflowEditorDialog = ({
	workflowName,
	projectType,
	onClose,
	onOpenDefaultEditorDialog,
	workflowId,
	returnFocusRef,
	workflowPublicationStatus = WorkflowPublicationStatus.None,
	shouldRedirectToDraftMode = true,
}: Props) => {
	return (
		<JSErrorBoundary
			id="jsm-error-boundary-default-workflow-editor-dialog"
			packageName="workflow-editor-preference-settings"
			teamName="jsd-shield"
			fallback="flag"
		>
			<DefaultWorkflowEditorDialogInner
				workflowName={workflowName}
				projectType={projectType}
				onClose={onClose}
				onOpenDefaultEditorDialog={onOpenDefaultEditorDialog}
				workflowId={workflowId}
				workflowPublicationStatus={workflowPublicationStatus}
				shouldRedirectToDraftMode={shouldRedirectToDraftMode}
				returnFocusRef={returnFocusRef}
			/>
		</JSErrorBoundary>
	);
};

const headingStyles = xcss({
	fontWeight: 'font.weight.medium',
});

const boxStyles = xcss({
	borderStyle: 'solid',
	borderRadius: 'radius.small',
	borderWidth: 'border.width',
	borderColor: 'color.border',
	paddingLeft: 'space.250',
	paddingRight: 'space.250',
	paddingTop: 'space.150',
	paddingBottom: 'space.150',
	flex: 1,
});

const legendStyles = css({
	paddingTop: token('space.200'),
	paddingBottom: token('space.200'),
	paddingLeft: token('space.0'),
	paddingRight: token('space.0'),
});

const warningContainerStyles = xcss({
	display: 'flex',
	paddingTop: 'space.100',
	paddingRight: 'space.0',
	paddingBottom: 'space.100',
	paddingLeft: 'space.0',
});

const warningTextStyles = xcss({
	marginLeft: 'space.100',
	flexGrow: 1,
	color: 'color.text.warning',
});
